import { apiClient } from '@lib/api/client';
import type {
  User,
  UserListResponse,
  UserSearchParams,
  AludraUser,
} from '../types/user.types';

/**
 * Map Aludra API user to frontend user format
 */
function mapAludraUserToUser(aludraUser: any): User {
  // Map roles from UserRoles array (from Aludra API)
  const userRoles = aludraUser.UserRoles?.map((ur: any) => ur.RoleName) || [];
  const roles = userRoles.length > 0 ? userRoles : (aludraUser.Roles || (aludraUser.Role ? [aludraUser.Role] : []));

  // Get company info from Companies array
  const company = aludraUser.Companies?.[0] || {};

  // Map branch offices - extract IDs from BranchOffice array
  const branchOffices = aludraUser.BranchOffice?.map((bo: any) =>
    bo.BranchOfficeId || bo.Id || bo.id
  ).filter((id: any) => id) || [];

  return {
    id: aludraUser.Id || aludraUser.UserId,
    email: aludraUser.Email || aludraUser.UserName,
    idNumber: aludraUser.IDNumber || '',
    firstName: aludraUser.FirstName || '',
    lastName: aludraUser.LastName || '',
    role: roles[0] || 'USER',
    roles: roles,
    companyId: company.CompanyId || aludraUser.CompanyId || '',
    companyName: company.Name || aludraUser.CompanyName || '',
    companyCode: company.Code || aludraUser.CompanyCode || '',
    avatar: aludraUser.Avatar || aludraUser.ProfileImage || null,
    phone: aludraUser.Phone || aludraUser.PhoneNumber || '',
    isActive: aludraUser.Status !== undefined ? aludraUser.Status : (aludraUser.IsActive !== undefined ? aludraUser.IsActive : true),
    branchOffices: branchOffices.length > 0 ? branchOffices : undefined,
    createdAt: aludraUser.CreatedAt || new Date().toISOString(),
    updatedAt: aludraUser.UpdatedAt || new Date().toISOString(),
  };
}

export const userService = {
  /**
   * Search users from Aludra API
   * The backend will automatically use the selected company
   */
  async getUsers(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    role?: string;
  }): Promise<UserListResponse> {
    // Strategy: Fetch ALL users at once (SearchText: null) because Aludra's search by name doesn't work
    // Return ALL users - filtering and pagination will be done in the component for instant updates
    const searchParams: Partial<UserSearchParams> = {
      SearchText: null, // Always fetch all users - client-side filtering will handle search
      PageNumber: 1,
      PageSize: 1000, // Large number to get all users in one request
      Roles: params?.role
        ? [params.role]
        : [
            // Vendedor
            'VENDEDOR', 'Vendedor', 'vendedor',
            // Customer B2B
            'CUSTOMER B2B', 'Customer B2B', 'Customer b2b', 'customer b2b',
            'CUSTOMERB2B', 'CustomerB2B', 'customerb2b',
            'CUSTOMER_B2B', 'Customer_B2B', 'customer_b2b',
            // Company Admin
            'COMPANY ADMIN', 'Company Admin', 'Company admin', 'company admin',
            'COMPANYADMIN', 'CompanyAdmin', 'companyadmin',
            'COMPANY_ADMIN', 'Company_Admin', 'company_admin',
            // Administrator
            'ADMINISTRATOR', 'Administrator', 'administrator',
            'ADMIN', 'Admin', 'admin',
          ],
    };

    console.log('🔍 Searching users with params:', searchParams);

    const response = await apiClient.post<{
      status: string;
      data: {
        Data: AludraUser[];
      };
    }>('/users/search', searchParams);

    console.log('📥 Search response:', response.data);

    const aludraUsers = response.data.data.Data || [];
    console.log('📥 Raw Aludra users:', aludraUsers);

    const allUsers = aludraUsers.map(mapAludraUserToUser);

    console.log('📊 Total users fetched:', allUsers.length);
    console.log('👥 Mapped users:', allUsers.map(u => ({ email: u.email, roles: u.roles })));

    // Return ALL users - component will handle filtering and pagination
    return {
      users: allUsers,
      total: allUsers.length,
      page: 1,
      pageSize: allUsers.length,
      hasMorePages: false,
    };
  },

  async getUserById(id: string): Promise<User> {
    // This endpoint is not yet implemented in Aludra API
    // We could search users and filter by ID
    const response = await this.getUsers({ pageSize: 1000 });
    const user = response.users.find((u) => u.id === id);

    if (!user) {
      throw new Error('User not found');
    }

    return user;
  },

  async createUser(user: Partial<User> & {
    email: string;
    idNumber: string;
    firstName: string;
    lastName: string;
    roles: string[];
    branchOffices: string[];
  }): Promise<User> {
    const payload = {
      Email: user.email,
      IDNumber: user.idNumber,
      FirstName: user.firstName,
      LastName: user.lastName,
      RolesName: user.roles,
      BranchOffice: user.branchOffices.map((id) => ({ BranchOfficeId: id })),
    };

    console.log('📤 Creating user with payload:', payload);
    console.log('📤 Roles being sent:', user.roles);

    const response = await apiClient.post<{
      status: string;
      message: string;
      data: any;
    }>('/users', payload);

    console.log('✅ Create user response:', response.data);

    // Return a basic user object - the actual data will be fetched in the refresh
    return {
      id: response.data.data?.Id || '',
      email: user.email,
      idNumber: user.idNumber,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.roles[0],
      roles: user.roles,
      companyId: '',
      companyName: '',
      companyCode: '',
      avatar: null,
      phone: '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },

  async getRoles(): Promise<Array<{ id: string; name: string; description: string }>> {
    const response = await apiClient.get<{
      status: string;
      data: Array<{ id: string; name: string; description: string }>;
    }>('/users/roles');

    return response.data.data;
  },

  async getBranchOffices(): Promise<Array<{ Id: string; Name: string; Code: string }>> {
    const response = await apiClient.get<{
      branchOffices: Array<any>;
      total: number;
      page: number;
      pageSize: number;
    }>('/companies/branch-offices');

    console.log('🏪 Branch offices response:', response.data);
    console.log('🏪 Branch offices data:', response.data.branchOffices);
    console.log('🏪 Branch offices count:', response.data.branchOffices?.length || 0);

    // Map the branch offices to the format expected by the UI
    return response.data.branchOffices.map((bo: any) => ({
      Id: bo.id,
      Name: bo.name,
      Code: bo.code,
    }));
  },

  async updateUser(id: string, user: Partial<User> & {
    email: string;
    idNumber: string;
    firstName: string;
    lastName: string;
    roles: string[];
    branchOffices: string[];
    password?: string;
  }): Promise<User> {
    await apiClient.put<{
      status: string;
      message: string;
      data: any;
    }>(`/users/${id}`, {
      Email: user.email,
      IDNumber: user.idNumber,
      FirstName: user.firstName,
      LastName: user.lastName,
      RolesName: user.roles,
      BranchOffice: user.branchOffices.map((boId) => ({ BranchOfficeId: boId })),
      ...(user.password ? { Password: user.password } : {}),
    });

    // Return a basic user object - the actual data will be fetched in the refresh
    return {
      id: id,
      email: user.email,
      idNumber: user.idNumber,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.roles[0],
      roles: user.roles,
      companyId: '',
      companyName: '',
      companyCode: '',
      avatar: null,
      phone: '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  async reactivateUser(id: string): Promise<void> {
    await apiClient.post(`/users/${id}/reactivate`);
  },
};
